import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { User, LogOut } from "lucide-react";
import { User as SupabaseUser } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { ProfileDialog } from "./ProfileDialog";

interface UserProfileDropdownProps {
  user: SupabaseUser;
  onLogout: () => void;
}

export const UserProfileDropdown = ({ user, onLogout }: UserProfileDropdownProps) => {
  const [showProfile, setShowProfile] = useState(false);
  const [displayName, setDisplayName] = useState("");

  useEffect(() => {
    fetchDisplayName();
  }, [user]);

  const fetchDisplayName = async () => {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('id', user.id)
        .single();

      setDisplayName(data?.display_name || user.email?.charAt(0).toUpperCase() || "U");
    } catch (error) {
      setDisplayName(user.email?.charAt(0).toUpperCase() || "U");
    }
  };

  const getInitials = () => {
    if (displayName && displayName.length > 1) {
      const words = displayName.split(' ');
      if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
      }
      return displayName.slice(0, 2).toUpperCase();
    }
    return displayName.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || "U";
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 rounded-full">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="text-xs font-medium">
                {getInitials()}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => setShowProfile(true)}>
            <User className="h-4 w-4 mr-2" />
            Profile
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ProfileDialog
        open={showProfile}
        onOpenChange={setShowProfile}
        user={user}
      />
    </>
  );
};